import { t } from '@/lib/i18n';
import { Link } from '@tanstack/react-router';
import { Check, Star, Download } from 'lucide-react';
import type { AssetWithUrls } from '@shared/types';
import { KIND_ICON, KIND_TINT, canDisplayRaw, cn, formatFileSize, tagText } from '@/lib/format';
import { setAssetDrag } from '@/lib/drag';
import { useSelection } from '@/lib/selection';
import { useFavouriteIds, useToggleFavourite } from '@/lib/queries';

/**
 * The asset grid. Thumbnails for images; a kind icon tile for everything else.
 * Each tile links to the asset detail, shows a confirmed-tag badge, and has a
 * hover/active favourite star.
 */
/**
 * Tags ride on AssetWithUrls now that every listing endpoint returns them, so
 * the grid takes plain assets. AI Search passes a richer tag record, which is
 * structurally compatible — the extra fields are simply unused here.
 */
/**
 * `newTab`: tiles open the asset in a new browser tab. AI Search uses it, since
 * its results are slow to rebuild and opening an asset there would throw them away.
 */
export function AssetGrid({ assets, newTab = false }: { assets: AssetWithUrls[]; newTab?: boolean }) {
  const { data: favIds } = useFavouriteIds();
  const toggleFav = useToggleFavourite();
  const selection = useSelection();

  // Default `items-stretch` (i.e. no items-* class): every tile in a row takes
  // the row's height, so a two-line caption beside a one-line caption leaves two
  // equal cards rather than a ragged edge.
  //
  // This was briefly `items-start` to stop a very tall thumbnail inflating its
  // whole row. That is now handled properly at the source — the thumbnail box is
  // aspect-square with overflow-hidden and the image is absolutely positioned —
  // so stretching is safe again and no longer lets one odd image distort a row.
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-3 p-4">
      {assets.map((a) => {
        const Icon = KIND_ICON[a.kind];
        const fav = favIds?.has(a.id) ?? false;
        const brand = tagText(a.aiTags?.brand);
        const imageType = tagText(a.aiTags?.imageType);
        return (
          <Link
            key={a.id}
            to="/assets/$assetId"
            params={{ assetId: a.id }}
            target={newTab ? '_blank' : undefined}
            rel={newTab ? 'noopener' : undefined}
            // Drag a tile onto a folder in the rail to move it. The tile stays a
            // link, so a plain click still opens the asset.
            draggable
            onDragStart={(e) =>
              setAssetDrag(e, {
                id: a.id,
                fileName: a.fileName,
                folderId: a.folderId,
                // Dragging a tile that is part of a selection moves the whole
                // selection; dragging an unselected tile moves just that one.
                ids: selection.isSelected(a.id) ? [...selection.ids] : undefined,
              })
            }
            className={cn(
              'group relative flex flex-col overflow-hidden rounded-[var(--radius-card)] surface-card border line transition-shadow hover:shadow-md',
              selection.isSelected(a.id) && 'ring-2 ring-brand-500',
            )}
          >
            {/* overflow-hidden and a hard aspect ratio: a very tall thumbnail
                (a 1L carton shot is ~3:1) was stretching its tile far past the
                others, breaking the row and pushing its caption down the page.
                The box is square first; the image fits inside it. */}
            <div className="relative flex aspect-square w-full items-center justify-center overflow-hidden surface-sunken">
              {a.thumbUrl ? (
                // Photos fill the tile; documents are letterboxed instead.
                // Artwork dielines and slides are wide, and cropping them to a
                // square cuts off the edges that distinguish one from the next.
                //
                // absolute inset-0 rather than size-full: as a flex child the
                // image could impose its own intrinsic height on the container,
                // which is what let the tall ones escape the square.
                <img
                  src={a.thumbUrl}
                  alt={a.fileName}
                  loading="lazy"
                  className={cn(
                    'absolute inset-0 size-full',
                    a.kind === 'image' && canDisplayRaw(a.mime) ? 'object-cover' : 'object-contain p-2',
                  )}
                />
              ) : (
                <Icon className={cn('size-12', KIND_TINT[a.kind])} aria-hidden />
              )}
              {a.aiTags?.confirmed && (
                <span className="absolute right-1.5 top-1.5 rounded-full bg-brand-500 px-1.5 py-px text-[9px] font-semibold text-white">✓ {t('assetGrid.tags')}</span>
              )}
              {/* Selection tick. Shown on hover, or always once selected, so the
                  grid stays clean until someone starts choosing. */}
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); selection.toggle(a.id); }}
                className={cn(
                  'absolute right-1.5 bottom-1.5 grid size-6 place-items-center rounded border-2 transition-opacity',
                  selection.isSelected(a.id)
                    ? 'border-brand-500 bg-brand-500 text-white opacity-100'
                    : 'border-white/70 bg-black/30 text-transparent opacity-0 group-hover:opacity-100',
                )}
                title={selection.isSelected(a.id) ? t('assetGrid.deselect') : t('assetGrid.select')}
                aria-label={selection.isSelected(a.id) ? t('assetGrid.deselect') : t('assetGrid.select')}
              >
                <Check className="size-3.5" />
              </button>
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFav.mutate(a.id); }}
                className={cn(
                  'absolute left-1.5 top-1.5 grid size-7 place-items-center rounded-full bg-black/40 text-white/90 backdrop-blur transition-opacity hover:bg-black/60',
                  fav ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
                )}
                title={fav ? t('assetGrid.removeFavourite') : t('assetGrid.addFavourite')}
                aria-label={fav ? t('assetGrid.removeFavourite') : t('assetGrid.addFavourite')}
              >
                <Star className={cn('size-4', fav && 'fill-amber-400 text-amber-400')} />
              </button>
              {/* Quick download of the original, without opening the asset. */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // An <a download> click saves the file; navigating would just show
                  // an image in the tab (the endpoint serves it inline for previews).
                  const link = document.createElement('a');
                  link.href = `/api/assets/${a.id}/raw?download=1`;
                  link.download = a.fileName;
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                }}
                className="absolute bottom-1.5 left-1.5 grid size-7 place-items-center rounded-full bg-black/40 text-white/90 opacity-0 backdrop-blur transition-opacity hover:bg-black/60 group-hover:opacity-100 focus-visible:opacity-100"
                title={t('zip.downloadFile')}
                aria-label={t('zip.downloadFile')}
              >
                <Download className="size-4" />
              </button>
            </div>
            {/* flex-1 so this block absorbs the row's spare height, and the chip
                row below is pushed to the bottom with mt-auto. Without it the
                card stretches but its contents stay top-aligned, so the tags in
                a row still sit at different heights. */}
            <div className="flex flex-1 flex-col gap-0.5 p-2">
              {/* Three lines at 11px before ellipsis. `truncate` sets
                  white-space:nowrap, so it cut names like
                  "Kallo - Cereals - Superfruit Granola 360g" at roughly a third
                  of the width no matter how much room the tile had. line-clamp
                  wraps first and only then ellipsises, and break-words lets the
                  long underscore/hyphen-joined names break at all — they often
                  have no natural break opportunity.
                  leading-tight keeps three lines from adding much height. */}
              <span
                className="line-clamp-3 break-words text-[11px] font-medium leading-tight text-strong"
                title={a.fileName}
              >
                {a.fileName}
              </span>
              <span className="text-[10px] text-faint">{t(`assetKind.${a.kind}`)} · {formatFileSize(a.sizeBytes)}</span>
              {/* Brand and image type, when the caller loaded them. Tinted apart
                  so the two read as different fields at a glance rather than as
                  one run of grey text. */}
              {(brand || imageType) && (
                // mt-auto pins the chips to the card's bottom edge, so they line
                // up across a row however many lines the filename above took.
                <span className="mt-auto flex flex-wrap gap-1 pt-0.5">
                  {brand && (
                    <span
                      title={t('assetGrid.brandLabel', { brand })}
                      className="max-w-full truncate rounded-full bg-brand-50 px-1.5 py-px text-[9px] font-medium text-brand-800 dark:bg-brand-900/40 dark:text-brand-100"
                    >
                      {brand}
                    </span>
                  )}
                  {imageType && (
                    <span
                      title={t('assetGrid.imageTypeLabel', { imageType })}
                      className="max-w-full truncate rounded-full bg-sky-50 px-1.5 py-px text-[9px] font-medium text-sky-700 dark:bg-sky-500/15 dark:text-sky-300"
                    >
                      {imageType}
                    </span>
                  )}
                </span>
              )}
            </div>
          </Link>
        );
      })}
    </div>
  );
}
